import React, { useState, useEffect } from 'react';
import './BillPreviewModal.css';

const BillPreviewModal = ({ isOpen, onClose, items, onApply }) => {
  const [localItems, setLocalItems] = useState([]);

  useEffect(() => {
    if (items) {
      // Create local deep copy for editing in modal
      setLocalItems(
        items.map((it) => {
          const qty = it.quantity > 0 ? it.quantity : 1;
          const mrp = parseFloat(it.mrp) || 0;
          return {
            ...it,
            quantity: qty,
            total: qty * mrp,
          };
        })
      );
    }
  }, [items, isOpen]);

  if (!isOpen) return null;

  // Global adjust all items
  const handleAdjustAll = (operation) => {
    setLocalItems((prev) =>
      prev.map((item) => {
        let newQty = item.quantity;
        if (operation === 'decrement') {
          newQty = Math.max(1, (newQty || 1) - 1);
        } else if (operation === 'increment') {
          newQty = (newQty || 1) + 1;
        } else if (operation === 'half') {
          newQty = Math.max(1, Math.round((newQty || 1) / 2));
        } else if (operation === 'double') {
          newQty = Math.max(1, (newQty || 1) * 2);
        }
        const mrp = parseFloat(item.mrp) || 0;
        return {
          ...item,
          quantity: newQty,
          total: newQty * mrp,
        };
      })
    );
  };

  // Item level adjust
  const handleItemAdjust = (index, operation) => {
    setLocalItems((prev) => {
      const updated = [...prev];
      let newQty = updated[index].quantity;
      if (operation === 'decrement') {
        newQty = Math.max(1, (newQty || 1) - 1);
      } else if (operation === 'increment') {
        newQty = (newQty || 1) + 1;
      } else if (operation === 'half') {
        newQty = Math.max(1, Math.round((newQty || 1) / 2));
      } else if (operation === 'double') {
        newQty = Math.max(1, (newQty || 1) * 2);
      }
      const mrp = parseFloat(updated[index].mrp) || 0;
      updated[index] = {
        ...updated[index],
        quantity: newQty,
        total: newQty * mrp,
      };
      return updated;
    });
  };

  // Item level direct input
  const handleQtyInput = (index, val) => {
    const num = parseInt(val, 10);
    setLocalItems((prev) => {
      const updated = [...prev];
      const newQty = isNaN(num) || num < 1 ? 1 : num;
      const mrp = parseFloat(updated[index].mrp) || 0;
      updated[index] = {
        ...updated[index],
        quantity: newQty,
        total: newQty * mrp,
      };
      return updated;
    });
  };

  // Delete item
  const handleDeleteItem = (index) => {
    setLocalItems((prev) => prev.filter((_, i) => i !== index));
  };

  const grandTotal = localItems.reduce((acc, it) => acc + (it.total || 0), 0);
  const totalUnits = localItems.reduce((acc, it) => acc + (it.quantity || 0), 0);

  const handleApplyChanges = () => {
    onApply(localItems);
    onClose();
  };

  return (
    <div className="preview-modal-backdrop" onClick={onClose}>
      <div className="preview-modal-card" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="preview-modal-header">
          <div className="preview-modal-header-info">
            <h3 className="preview-modal-title">Prescription Preview</h3>
            <span className="preview-modal-item-count">
              {localItems.length} {localItems.length === 1 ? 'item' : 'items'}
            </span>
          </div>
          <button className="preview-modal-close-icon" onClick={onClose} title="Close">
            ✕
          </button>
        </div>

        {/* Adjust All Toolbar */}
        <div className="preview-modal-toolbar">
          <span className="toolbar-label">Adjust All:</span>
          <div className="toolbar-btn-group">
            <button
              type="button"
              className="toolbar-action-btn"
              onClick={() => handleAdjustAll('decrement')}
              title="Decrease all item quantities by 1"
            >
              −
            </button>
            <button
              type="button"
              className="toolbar-action-btn"
              onClick={() => handleAdjustAll('increment')}
              title="Increase all item quantities by 1"
            >
              +
            </button>
            <button
              type="button"
              className="toolbar-action-btn"
              onClick={() => handleAdjustAll('half')}
              title="Cut all quantities in half"
            >
              Half
            </button>
            <button
              type="button"
              className="toolbar-action-btn"
              onClick={() => handleAdjustAll('double')}
              title="Double all quantities"
            >
              Double
            </button>
          </div>
        </div>

        {/* Table Body */}
        <div className="preview-modal-body">
          {localItems.length === 0 ? (
            <div className="preview-empty-state">No items selected</div>
          ) : (
            <table className="preview-modal-table">
              <thead>
                <tr>
                  <th style={{ width: '45px', textAlign: 'center' }}>#</th>
                  <th>Item Name</th>
                  <th style={{ textAlign: 'center', width: '140px' }}>Quantity</th>
                  <th style={{ textAlign: 'right', width: '120px' }}>Price</th>
                  <th style={{ textAlign: 'right', width: '130px' }}>Total Price</th>
                  <th style={{ width: '45px', textAlign: 'center' }}></th>
                </tr>
              </thead>
              <tbody>
                {localItems.map((item, idx) => (
                  <tr key={idx} className="preview-table-row">
                    <td style={{ textAlign: 'center', color: '#64748b' }}>{idx + 1}</td>
                    <td>
                      <div className="item-name-cell">
                        <strong className="item-name-text">{item.name}</strong>
                        {item.batch && (
                          <span className="item-batch-tag">Batch: {item.batch}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="qty-control-inline">
                        <button
                          type="button"
                          className="qty-btn-stepper"
                          onClick={() => handleItemAdjust(idx, 'decrement')}
                          title="Decrease"
                        >
                          −
                        </button>
                        <input
                          type="number"
                          min="1"
                          value={item.quantity}
                          onChange={(e) => handleQtyInput(idx, e.target.value)}
                          className="qty-input-box"
                        />
                        <button
                          type="button"
                          className="qty-btn-stepper"
                          onClick={() => handleItemAdjust(idx, 'increment')}
                          title="Increase"
                        >
                          +
                        </button>
                      </div>
                    </td>
                    <td style={{ textAlign: 'right' }} className="price-col">
                      ₹ {(parseFloat(item.mrp) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td style={{ textAlign: 'right' }} className="total-col">
                      <strong>₹ {(item.total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="item-remove-btn"
                        onClick={() => handleDeleteItem(idx)}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pricing Summary */}
          {localItems.length > 0 && (
            <div className="preview-summary-footer">
              <div className="summary-details">
                <span>Total Units: <strong>{totalUnits}</strong></span>
              </div>
              <div className="summary-grand-total">
                <span className="grand-label">Total Price:</span>
                <span className="grand-val">
                  ₹ {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Modal Actions */}
        <div className="preview-modal-footer">
          <button type="button" className="preview-btn-cancel" onClick={onClose}>
            Close
          </button>
          <button
            type="button"
            className="preview-btn-apply"
            onClick={handleApplyChanges}
            disabled={localItems.length === 0}
          >
            Apply & Update
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillPreviewModal;
